import { getCurrencyFormat, getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate } from 'ppbl/utils/string-helper'
import {
  WorldReady,
  NameFormat,
  AddressFormat,
  PhoneNumberFormat,
} from '@paypalcorp/worldready'
import serverData from 'ppbl/utils/server-data'
import {
  worldReadyValidationCases,
  exampleTotalAnnualBusinessRevenue,
} from './constants'

export const required = (value, label) => {
  return value ? undefined : `${label} is required`
}

export const validFedTaxIDFormat = (taxId, label) => {
  const taxIdRegex = /^\d{2}-\d{7}$/

  return taxIdRegex.test(taxId)
    ? undefined
    : interpolate(getLocalizedText('validation.isInvalidErrorText'), {
        label,
      })
}

export const validSSNFormat = (ssn, label) => {
  const ssnRegex = /^\d{3}-\d{2}-\d{4}$/

  return ssnRegex.test(ssn)
    ? undefined
    : interpolate(getLocalizedText('validation.isInvalidErrorText'), {
        label,
      })
}

export const validFundingCode = (value, label) => {
  const FundingCodeRegex = /^[a-zA-Z0-9]{5}-[a-zA-Z][0-9]{1,9}$/
  if (value) {
    return FundingCodeRegex.test(value)
      ? undefined
      : interpolate(getLocalizedText('validation.isInvalidErrorText'), {
          label,
        })
  }
  return undefined
}

export const validEmail = (value, label) => {
  const parts = value.split('@')
  if (parts.length !== 2) {
    return interpolate(
      getLocalizedText('validation.emailValidationErrorText'),
      {
        label,
      },
    )
  }

  const [local, domain] = parts
  const isValidLocal = local.length > 0
  const isValidDomain =
    domain.includes('.') && domain.split('.').every((part) => part.length > 0)

  return isValidLocal && isValidDomain
    ? undefined
    : interpolate(getLocalizedText('validation.emailValidationErrorText'), {
        label,
      })
}

export const validPhone = (value, label) => {
  return value.length === 14 &&
    value.charAt(0) === '(' &&
    !isNaN(value.substring(1, 4)) &&
    value.charAt(4) === ')' &&
    value.charAt(5) === ' ' &&
    !isNaN(value.substring(6, 9)) &&
    value.charAt(9) === '-' &&
    !isNaN(value.substring(10, 14))
    ? undefined
    : interpolate(
        getLocalizedText('validation.phoneNumberValidationErrorText'),
        {
          label,
        },
      )
}

export const validZipCode = (value, label) => {
  const cleanedZip = value.replace(/\D/g, '')
  return cleanedZip.length === 5
    ? undefined
    : interpolate(getLocalizedText('validation.zipCodeValidationErrorText'), {
        label,
      })
}

const isValidDate = (dateString) => {
  const parts = dateString.split('/')
  const todayDate = new Date()
  if (parts.length === 2 || parts.length === 3) {
    let [month, day = 1, year] = parts.map(Number)
    if (parts.length === 2) {
      ;[month, year] = parts.map(Number)
    }
    const isValidYear = year >= 1900 && year <= todayDate.getFullYear()
    let isValidMonth = false
    if (year === todayDate.getFullYear()) {
      isValidMonth = month >= 1 && month <= todayDate.getMonth() + 1
    } else {
      isValidMonth = month >= 1 && month <= 12
    }
    const isValidDay = day >= 1 && day <= new Date(year, month, 0).getDate()

    return parts.length === 2
      ? isValidMonth && isValidYear
      : isValidMonth && isValidYear && isValidDay
  }
  return undefined
}

const isOlderThan18 = (dateString, todayDate) => {
  const dob = new Date(dateString)
  let age = todayDate.getFullYear() - dob.getFullYear()
  if (
    todayDate.getMonth() < dob.getMonth() ||
    (todayDate.getMonth() === dob.getMonth() &&
      todayDate.getDate() < dob.getDate())
  ) {
    age--
  }
  return age > 18
}

const isValidDoB = (dateString) => {
  const parts = dateString.split('/')
  const todayDate = new Date()
  if (parts.length === 3) {
    let [month, day, year] = parts.map(Number)
    const isValidMonth = month >= 1 && month <= 12
    const isValidYear = year >= 1900 && year <= todayDate.getFullYear()
    const isValidDay = day >= 1 && day <= new Date(year, month, 0).getDate()
    const isValidAge = isOlderThan18(dateString, todayDate)

    return isValidMonth && isValidYear && isValidDay && isValidAge
  }
  return undefined
}

export const isDate = (value, label) => {
  if (!isValidDate(value))
    return interpolate(getLocalizedText('validation.dateValidationErrorText'), {
      label,
    })
  return undefined
}

export const isDOB = (value, label) => {
  if (!isValidDoB(value))
    return interpolate(getLocalizedText('validation.dobValidationErrorText'), {
      label,
    })
  return undefined
}

export const maxLength = (max) => (value, label) => {
  if (value !== null && value.length > max)
    return interpolate(
      getLocalizedText('validation.lengthValidationErrorText'),
      {
        label,
        max,
      },
    )
  return undefined
}

export const convertUnicodeToChar = (unicode) => {
  if (unicode !== undefined) {
    const hexCode = parseInt(unicode.replace('U+', ''), 16)
    return String.fromCodePoint(hexCode)
  }
  return ''
}

export const worldReadyValidationMessages = (
  invalidCode,
  fieldLabel,
  invalidCodePoint,
) => {
  if (
    [
      'Home address',
      'Home address2',
      'City',
      'State',
      'Zip',
      'Business address',
      'Business address2',
    ].includes(fieldLabel)
  ) {
    switch (invalidCode) {
      case worldReadyValidationCases.MISSING_REQUIRED:
        return `Please enter a valid ${fieldLabel}`
      case worldReadyValidationCases.ADDRESS_CONTAINS_PO_BOX:
        return `${fieldLabel} cannot be a PO Box`
      default:
        return `Please enter a valid ${fieldLabel}`
    }
  }
  switch (invalidCode) {
    case worldReadyValidationCases.MISSING_REQUIRED:
      return `${fieldLabel} is Required`
    case worldReadyValidationCases.INVALID_SCRIPT:
      return `${convertUnicodeToChar(
        invalidCodePoint,
      )} is not allowed in ${fieldLabel}`
    case worldReadyValidationCases.INVALID_ALL_NON_VOWEL:
      return `${fieldLabel} must contain a vowel`
    case worldReadyValidationCases.INVALID_MAX_LENGTH_EXCEEDED:
      return `${fieldLabel} contains too many characters`
    case worldReadyValidationCases.PHONE_ALPHA_CHARACTERS:
      return `${fieldLabel} should not contain alpha characters`
    case worldReadyValidationCases.ADDRESS_CONTAINS_PO_BOX:
      return `${fieldLabel} cannot be a PO Box`
    default:
      return `Please enter a valid ${fieldLabel}`
  }
}

export const businessWorldreadyValidation = (value, label) => {
  const worldReadyLocale = serverData.locality.locale
  const worldReady = new WorldReady(worldReadyLocale)
  try {
    let businessNameFormatter = new NameFormat(worldReady, {
      nameRegion: 'US',
      type: 'business',
    })
    if (
      businessNameFormatter.validate({
        businessName: value,
      })[0]
    ) {
      const validationCode = businessNameFormatter.validate({
        businessName: value,
      })[0]
      return worldReadyValidationMessages(
        validationCode.code,
        label,
        validationCode?.invalidCodePoint,
      )
    }
  } catch (error) {
    return error
  }
  return undefined
}

export const getFirstNameWorldreadyValidation = (value, label) => {
  const worldReadyLocale = serverData.locality.locale
  const worldReady = new WorldReady(worldReadyLocale)
  try {
    let nameFormatter = new NameFormat(worldReady, {
      nameRegion: 'US',
    })
    if (
      nameFormatter.validate({ givenName: value })[0].element === 'givenName'
    ) {
      let validationCode = nameFormatter.validate({
        givenName: value,
      })[0]
      return worldReadyValidationMessages(
        validationCode?.code,
        label,
        validationCode?.invalidCodePoint,
      )
    }
  } catch (error) {
    return error
  }
  return undefined
}

export const getLastNameWorldreadyValidation = (value, label) => {
  const worldReadyLocale = serverData.locality.locale
  const worldReady = new WorldReady(worldReadyLocale)
  try {
    let nameFormatter = new NameFormat(worldReady, {
      nameRegion: 'US',
    })
    let lastNameFormatter = nameFormatter
      .validate({ surname: value })
      .filter((item) => item.element === 'surname')
    if (lastNameFormatter[0]?.element === 'surname') {
      return worldReadyValidationMessages(
        lastNameFormatter[0]?.code,
        label,
        lastNameFormatter[0]?.invalidCodePoint,
      )
    }
  } catch (error) {
    return error
  }
  return undefined
}

export const worldreadyPhoneValidation = (value, label) => {
  value = value ? value : ''
  try {
    let phoneNumberFormatter = new PhoneNumberFormat({ phoneRegion: 'US' })
    if (
      phoneNumberFormatter?.parse(value) &&
      phoneNumberFormatter?.parse(value)?.errors
    ) {
      let validationCode = phoneNumberFormatter?.parse(value)?.errors[0].code
      return worldReadyValidationMessages(validationCode, label)
    }
  } catch (error) {
    return error
  }
  return undefined
}

export const getAddressValidator = (value, field, label) => {
  const worldReadyLocale = serverData.locality.locale
  const worldReady = new WorldReady(worldReadyLocale)
  try {
    let addressFormatter = new AddressFormat(worldReady, {
      addressRegion: 'US',
    })
    const validator = addressFormatter
      .validate({ [field]: value })
      .filter((item) => item.element === field)
    if (validator[0]) {
      return worldReadyValidationMessages(validator[0]?.code, label)
    }
  } catch (error) {
    return error
  }
  return undefined
}

export const worldreadyAddressValidator = (value, label) => {
  const addressMapping = {
    'Home address': 'addressLine1',
    'Home address2': 'addressLine2',
    City: 'adminArea2',
    State: 'adminArea1',
    Zip: 'postalCode',
    'Business address': 'addressLine1',
    'Business address2': 'addressLine2',
  }

  return getAddressValidator(value ? value : '', addressMapping[label], label)
}

export const validationAddressPOBox = (value, label) => {
  const disallowPostOfficeBoxRegexPattern =
    /^(?!\s*[pP]\.?\s*[oO].?\s*[bB][oO][xX]).*$/
  return disallowPostOfficeBoxRegexPattern.test(value)
    ? undefined
    : interpolate(getLocalizedText('validation.isAddressContainsPOBox'), {
        label,
      })
}

export const validateWebAddress = (value, label) => {
  if (!value || value.length === 0) return undefined
  const urlRegex =
    /^(https?:\/\/)?([a-zA-Z0-9-]*\.)?([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.[^\s]+$/

  const isValidUrl = urlRegex.test(value)

  return isValidUrl
    ? undefined
    : interpolate(
        getLocalizedText('validation.webAddressValidationErrorText'),
        {
          label,
        },
      )
}

export const maxCurrencyVal = (max) => (value, label) => {
  if (Number(('' + value).replace(/\D/g, '')) > max)
    return interpolate(getLocalizedText('validation.maxCurrencyErrorText'), {
      label,
      max: getCurrencyFormat(max),
    })
  return undefined
}

export const minCurrencyVal = (min) => (value, label) => {
  if (Number(('' + value).replace(/\D/g, '')) < min)
    return interpolate(getLocalizedText('validation.minCurrencyErrorText'), {
      label,
      min: getCurrencyFormat(exampleTotalAnnualBusinessRevenue),
    })
  return undefined
}

export const valRange = (min, max) => (value, label) => {
  if (Number(value) < min || Number(value) > max)
    return interpolate(getLocalizedText('validation.valRangeErrorText'), {
      label,
      min,
      max,
    })
  return undefined
}

export const valRangeWithPercentage = (min, max) => (value, label) => {
  const numericValue = parseFloat(value.replace('%', '').trim())
  if (
    isNaN(numericValue) ||
    Number(numericValue) < min ||
    Number(numericValue) > max
  )
    return interpolate(getLocalizedText('validation.valRangeErrorText'), {
      label,
      min,
      max,
    })
  return undefined
}

export const scrollOnErrors = (errors) => {
  if (errors) {
    scroll({
      top: 100,
      behavior: 'smooth',
    })
  }
}

export const composeValidators =
  ({ label, validators }) =>
  (value) =>
    validators.reduce(
      (error, validator) => error || validator(value, label),
      undefined,
    )

export const composeValidatorWithValue = ({ label, validators, value }) =>
  validators.reduce(
    (error, validator) => error || validator(value, label),
    undefined,
  )
